/* ============================================================
 * <desktop-mega-nav> — James Oro desktop navigation
 *
 * Behavior:
 *  - Top-level nav items open on HOVER. Moving between adjacent
 *    items switches the open panel instantly so you can scan the
 *    whole nav without clicking.
 *  - 150ms grace period on close so moving the cursor from a
 *    trigger to its panel doesn't close it.
 *  - The Help (?) icon stays CLICK-only — hover-opening an icon
 *    you might just be passing through is jarring.
 *  - Click still toggles everything (keyboard, touch, fallback).
 *  - ESC closes and returns focus to the trigger.
 *  - Click outside the nav closes any open panel.
 * ============================================================ */

class DesktopMegaNav extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-jo-dnav-trigger]'));
    this.activeKey = null;
    this.closeTimer = null;
    this.CLOSE_DELAY_MS = 150;

    this.triggers.forEach((trigger) => {
      const key = trigger.getAttribute('data-jo-dnav-trigger');
      const panel = this.queryPanel(key);
      // Triggers whose key is "util" are click-only (the Help icon).
      const isClickOnly = key === 'util';

      // Click toggles (keyboard + touch + click-only triggers).
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle(key, trigger);
      });

      if (!isClickOnly) {
        trigger.addEventListener('mouseenter', () => this.open(key, trigger));
        trigger.addEventListener('mouseleave', () => this.scheduleClose());
        if (panel) {
          panel.addEventListener('mouseenter', () => this.cancelClose());
          panel.addEventListener('mouseleave', () => this.scheduleClose());
        }
      }
    });

    // Cart icon → open the cart drawer.
    // The drawer's click handler (cart-drawer.js) is bound to the legacy
    // header's #cart-icon-bubble. We can't reuse that id here (duplicate
    // ids break the single-element querySelector binding), so forward our
    // click to that legacy trigger. Calling .click() on it runs the
    // bound handler and opens the drawer even though it's display:none on
    // desktop. If no cart drawer is configured, let the link navigate.
    const cartIcon = this.querySelector('[data-jo-dnav-cart]');
    if (cartIcon) {
      cartIcon.addEventListener('click', (event) => {
        const legacyTrigger = document.getElementById('cart-icon-bubble');
        const drawer = document.querySelector('cart-drawer');
        if (legacyTrigger && drawer) {
          event.preventDefault();
          legacyTrigger.click();
        }
      });
    }

    // Click outside the nav closes everything.
    this.onDocClick = (event) => {
      if (this.activeKey && !this.contains(event.target)) {
        this.closeAll();
      }
    };
    document.addEventListener('click', this.onDocClick);

    this.onKeyUp = this.onKeyUp.bind(this);
    document.addEventListener('keyup', this.onKeyUp);
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('click', this.onDocClick);
    this.cancelClose();
  }

  queryTrigger(key) {
    return this.querySelector('[data-jo-dnav-trigger="' + CSS.escape(key) + '"]');
  }

  queryPanel(key) {
    return this.querySelector('[data-jo-dnav-panel="' + CSS.escape(key) + '"]');
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.closeAllImmediate(), this.CLOSE_DELAY_MS);
  }

  cancelClose() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  open(key, trigger) {
    this.cancelClose();
    if (this.activeKey === key) return;
    // Switching from one nav to another: close the previous immediately
    // so the swap reads as a single motion, no flicker.
    this.closeAllImmediate();
    const panel = this.queryPanel(key);
    if (!panel) return;
    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    this.activeKey = key;
  }

  toggle(key, trigger) {
    if (this.activeKey === key) {
      this.closeAll();
      return;
    }
    this.open(key, trigger);
  }

  closeAllImmediate() {
    if (!this.activeKey) return;
    this.querySelectorAll('[data-jo-dnav-panel]').forEach((p) => p.classList.remove('is-open'));
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.classList.remove('is-open');
    this.activeKey = null;
  }

  closeAll() {
    this.cancelClose();
    this.closeAllImmediate();
  }

  onKeyUp(event) {
    if (event.key === 'Escape' && this.activeKey) {
      const trigger = this.queryTrigger(this.activeKey);
      this.closeAll();
      if (trigger) trigger.focus();
    }
  }
}

if (!customElements.get('desktop-mega-nav')) {
  customElements.define('desktop-mega-nav', DesktopMegaNav);
}
